import { newIdempotencyKey } from '@micarahr/api-client';
import { apiTransport } from './api-client';
import type {
  Candidate,
  CandidateStage,
  CompensationOverview,
  CompensationRecord,
  JobRequisition,
  Money,
} from '@micarahr/shared-types';
import type { Page, PageRequest } from './collections';
import type { ConvertedEmployee } from './seed';
import type { AuditActor } from './audit';
import { narrow, COMPENSATION_CHANGE_TYPES } from './narrow';

/**
 * Recruitment and compensation.
 *
 * Two modules with one thing in common: both write facts that are expensive to
 * undo. Converting a candidate creates an employee and a salary; recording a
 * compensation change alters what somebody is paid from a date. Neither is a
 * form submission that can be repeated harmlessly, so both carry idempotency
 * keys and neither is optimistically reflected before the server answers.
 *
 * ---
 *
 * **Both are wired, and both keep the prototype's exact method names.**
 *
 * Compensation's signatures were migrated properly: the service no longer takes
 * `permissions` or an `AuditActor`, because both were the prototype asking a
 * component for things a server already knows. The mock reads the session for
 * the same information, so one shape serves both adapters.
 *
 * Recruitment keeps the prototype's shape instead — `requisitions`,
 * `candidates`, `moveStage(id, stage, actor)` — because the `{...mock, ...api}`
 * selector only overrides identical names, and a differently-named or
 * differently-typed method is a silent fall-through to the mock's empty store.
 * The audit actors the mock takes are accepted and never sent; the server
 * resolves the caller from the session.
 */

const CANDIDATE_STAGES = [
  'applied',
  'screening',
  'assessment',
  'interview',
  'final_interview',
  'reference_check',
  'offer',
  'hired',
  'rejected',
  'withdrawn',
] as const;

const OFFER_STATUSES = ['draft', 'sent', 'accepted', 'declined', 'expired', 'withdrawn'] as const;

/**
 * `extended` is what the server wrote for an open offer before the two sides
 * agreed on `sent`. Rows from then still carry it, and `narrow` refusing one
 * took the whole candidate board down with it — so it is read as the open
 * offer it is.
 */
function offerStatus(value: string): (typeof OFFER_STATUSES)[number] {
  return narrow(OFFER_STATUSES, value === 'extended' ? 'sent' : value, 'offer status');
}

/* The candidate panel's shapes. The mock builds the same ones. */

export type InterviewStatus = 'scheduled' | 'completed' | 'no_show' | 'cancelled';
export type InterviewRecommendation = 'advance' | 'hold' | 'do_not_advance';

export interface CandidateInterviewRecord {
  id: string;
  scheduledFor: string;
  kind: string;
  interviewerName: string | null;
  status: InterviewStatus;
  feedback: string | null;
  recommendation: InterviewRecommendation | null;
}

export interface CandidateStageEvent {
  id: string;
  fromStage: CandidateStage | null;
  toStage: CandidateStage;
  note: string | null;
  actorName: string | null;
  createdAt: string;
}

export interface CandidateDetail {
  candidate: Candidate;
  history: CandidateStageEvent[];
  interviews: CandidateInterviewRecord[];
  /**
   * Where a plain move can take this candidate, as the server decides it.
   * Offer and hired are never in it: they are reached by extending and by
   * accepting an offer, which carry the terms those stages mean.
   */
  allowedStages: CandidateStage[];
  /** Where the requisition's headcount sits — the likeliest department to hire into. */
  requisitionDepartmentId: string | null;
  /**
   * The rest of what the requisition decided about the hire, which creating
   * the employee starts from: the employment type it was approved as, where
   * the role sits and who it reports to.
   */
  requisitionPlacement: {
    /** The position the requisition is for — named, or found by its title. */
    positionId: string | null;
    employmentType: string | null;
    workLocationId: string | null;
    hiringManagerEmployeeId: string | null;
  } | null;
}

export interface NewCandidateInput {
  fullName: string;
  email: string;
  mobileNumber?: string;
  requisitionId?: string;
  source?: string;
  expectedMonthlyRate?: number;
  notes?: string;
}

export interface InterviewOutcomeInput {
  status: Exclude<InterviewStatus, 'scheduled'>;
  recommendation?: InterviewRecommendation;
  feedback: string;
}

const INTERVIEW_STATUSES = ['scheduled', 'completed', 'no_show', 'cancelled'] as const;
const RECOMMENDATIONS = ['advance', 'hold', 'do_not_advance'] as const;

interface ApiInterview {
  id: string;
  scheduledFor: string;
  kind: string;
  interviewerName: string | null;
  status: string;
  feedback: string | null;
  recommendation: string | null;
}

function toInterview(row: ApiInterview): CandidateInterviewRecord {
  return {
    id: row.id,
    scheduledFor: row.scheduledFor,
    kind: row.kind,
    interviewerName: row.interviewerName,
    status: narrow(INTERVIEW_STATUSES, row.status, 'interview status'),
    feedback: row.feedback,
    recommendation: row.recommendation
      ? narrow(RECOMMENDATIONS, row.recommendation, 'interview recommendation')
      : null,
  };
}

interface ApiRequisition {
  id: string;
  referenceCode: string;
  title: string;
  departmentId: string | null;
  departmentName?: string | null;
  positionId: string | null;
  positionTitle?: string | null;
  headcount: number;
  status: string;
  justification: string | null;
  employmentType: string | null;
  workLocationId: string | null;
  workLocationName?: string | null;
  hiringManagerEmployeeId: string | null;
  hiringManagerName?: string | null;
  hiringManagerLastWorkingDay?: string | null;
  hiringManagerHasLeft?: boolean;
  targetStartDate: string | null;
  budgetNote: string | null;
  createdByUserId?: string | null;
  createdByName?: string | null;
  approvedByName?: string | null;
  hiredCount?: number;
  approvedAt: string | null;
  createdAt: string;
  _count?: { candidates: number };
}

/**
 * Two vocabularies, one state machine.
 *
 * The server's requisition lifecycle is draft → approved; the screens' is
 * pending_approval → open. A freshly created requisition is awaiting approval
 * — creating *is* submitting, there is no separate submit step — and an
 * approved one is open for candidates, which the server enforces by refusing
 * candidates against anything else. So the translation maps states, not
 * labels: presenting a server "draft" as the frontend's editable-draft state
 * would hide the Approve button from the person whose approval it awaits.
 */
function toRequisitionStatus(value: string): JobRequisition['status'] {
  if (value === 'draft') return 'pending_approval';
  if (value === 'approved') return 'open';
  return narrow(
    ['pending_approval', 'open', 'on_hold', 'filled', 'cancelled'] as const,
    value,
    'requisition status',
  );
}

/**
 * The requisition as the server stores it, every name already resolved.
 *
 * The placement fields — type, place, hiring manager, target start, budget
 * note — used to be dropped on the way in and shown as blanks on the way out.
 * They are columns now, and the list route joins their names.
 */
function toRequisition(row: ApiRequisition): JobRequisition {
  return {
    id: row.id,
    referenceCode: row.referenceCode,
    title: row.title,
    positionId: row.positionId,
    positionTitle: row.positionTitle ?? null,
    departmentId: row.departmentId ?? '',
    departmentName: row.departmentName ?? '',
    hiringManagerEmployeeId: (row.hiringManagerEmployeeId ??
      null) as JobRequisition['hiringManagerEmployeeId'],
    hiringManagerName: row.hiringManagerName ?? null,
    hiringManagerLastWorkingDay: row.hiringManagerLastWorkingDay ?? null,
    hiringManagerHasLeft: row.hiringManagerHasLeft ?? false,
    headcount: row.headcount,
    // Hired candidates, not candidates: an applicant is not a filled seat.
    filledCount: row.hiredCount ?? 0,
    employmentType: row.employmentType ?? '',
    workLocationId: row.workLocationId,
    workLocationName: row.workLocationName ?? null,
    budgetNote: row.budgetNote,
    reason: row.justification ?? '',
    status: toRequisitionStatus(row.status),
    targetStartDate: row.targetStartDate
      ? (row.targetStartDate.slice(0, 10) as JobRequisition['targetStartDate'])
      : null,
    createdAt: row.createdAt,
    approvedAt: row.approvedAt,
    createdByUserId: row.createdByUserId ?? null,
    createdByName: row.createdByName ?? null,
    approvedByName: row.approvedByName ?? null,
    candidateCount: row._count?.candidates ?? 0,
  };
}

interface ApiCandidate {
  id: string;
  referenceCode: string;
  fullName: string;
  email: string;
  mobileNumber: string | null;
  source: string;
  stage: string;
  requisitionId: string | null;
  /** Joined on the list; absent from the move-stage response. */
  requisition?: {
    title: string;
    referenceCode: string;
    departmentId?: string | null;
    positionId?: string | null;
    employmentType?: string | null;
    workLocationId?: string | null;
    hiringManagerEmployeeId?: string | null;
  } | null;
  expectedMonthlyRate: string | null;
  offerMonthlyRate: string | null;
  offerStatus: string | null;
  offerStartDate: string | null;
  convertedEmployeeId: string | null;
  appliedAt: string;
  updatedAt: string;
}

function toCandidate(row: ApiCandidate): Candidate {
  return {
    id: row.id,
    referenceCode: row.referenceCode,
    fullName: row.fullName,
    email: row.email,
    mobileNumber: row.mobileNumber ?? '',
    requisitionId: row.requisitionId ?? '',
    requisitionTitle: row.requisition?.title ?? '',
    stage: narrow(CANDIDATE_STAGES, row.stage, 'candidate stage'),
    source: row.source,
    appliedAt: row.appliedAt,
    // Résumés and experience are not modelled on the API candidate. Null and
    // zero rather than invented facts; the card renders neither as a claim.
    resumeFileName: null,
    expectedMonthlyRate: toMoney(row.expectedMonthlyRate),
    yearsExperience: 0,
    // Scheduled through their own route; the list projection does not include
    // them, and an empty list is what this response actually contains.
    interviews: [],
    offer: row.offerStatus
      ? {
          // The offer lives on the candidate row, not as its own record; a
          // stable derived id keeps the screen's keys deterministic.
          id: `${row.id}:offer`,
          monthlyRate: toMoney(row.offerMonthlyRate) ?? { minorUnits: 0, currency: 'PHP' },
          startDate: (row.offerStartDate ?? '').slice(0, 10),
          positionTitle: row.requisition?.title ?? '',
          status: offerStatus(row.offerStatus),
          sentAt: null,
          respondedAt: null,
          notes: null,
        }
      : null,
    notes: [],
    rejectionReason: null,
    convertedEmployeeId: row.convertedEmployeeId,
    updatedAt: row.updatedAt,
  };
}

interface ApiRecruitmentDashboard {
  requisitionsByStatus: Record<string, number>;
  candidatesByStage: Record<string, number>;
  interviewsScheduled: number;
  /** Mean days from application to hire over hired candidates; null when none. */
  averageDaysToHire?: number | null;
  hiredCount?: number;
}

export const apiRecruitmentService = {
  async requisitions(
    filter: PageRequest & { status?: string } = {},
  ): Promise<Page<JobRequisition>> {
    // The route parses no query, so filtering happens on what it sent.
    const rows = await apiTransport().get<ApiRequisition[]>('/api/v1/job-requisitions');

    const mapped = rows
      .map(toRequisition)
      .filter((row) => !filter.status || filter.status === 'all' || row.status === filter.status);

    return {
      rows: mapped,
      total: mapped.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? mapped.length,
    };
  },

  /**
   * Raise a requisition, with every field the form asked for.
   *
   * The employment type goes over in the server's vocabulary (`fixed_term`,
   * not "Contractual"); the names are the server's to resolve.
   */
  async createRequisition(
    input: {
      title: string;
      departmentId: string;
      departmentName: string;
      /** The position it is for; the server finds it by title when absent. */
      positionId?: string | null;
      headcount: number;
      employmentType: string;
      workLocationId: string | null;
      workLocationName: string | null;
      hiringManagerEmployeeId: string | null;
      hiringManagerName: string | null;
      reason: string;
      targetStartDate: string | null;
      budgetNote: string | null;
    },
    _actor: AuditActor,
  ): Promise<JobRequisition> {
    const row = await apiTransport().post<ApiRequisition>(
      '/api/v1/job-requisitions',
      {
        title: input.title,
        departmentId: input.departmentId || undefined,
        positionId: input.positionId || undefined,
        headcount: input.headcount,
        justification: input.reason || undefined,
        employmentType: input.employmentType || undefined,
        workLocationId: input.workLocationId || undefined,
        hiringManagerEmployeeId: input.hiringManagerEmployeeId || undefined,
        targetStartDate: input.targetStartDate || undefined,
        budgetNote: input.budgetNote || undefined,
      },
      { idempotencyKey: newIdempotencyKey() },
    );
    return toRequisition(row);
  },

  async approveRequisition(id: string, _actor: AuditActor): Promise<JobRequisition> {
    return toRequisition(
      await apiTransport().post<ApiRequisition>(`/api/v1/job-requisitions/${id}/approve`, {}),
    );
  },

  async candidates(
    filter: PageRequest & { stage?: string; requisitionId?: string } = {},
  ): Promise<Page<Candidate>> {
    // No query is parsed by the route; it caps itself at 300 and the filters
    // happen here, on the same rows the board renders.
    const rows = (await apiTransport().get<ApiCandidate[]>('/api/v1/candidates')).map(toCandidate);

    const needle = filter.search?.trim().toLowerCase();
    const mapped = rows
      .filter((row) => !filter.stage || filter.stage === 'all' || row.stage === filter.stage)
      .filter((row) => !filter.requisitionId || row.requisitionId === filter.requisitionId)
      .filter(
        (row) =>
          !needle ||
          `${row.fullName} ${row.referenceCode} ${row.requisitionTitle} ${row.email}`
            .toLowerCase()
            .includes(needle),
      );

    return {
      rows: mapped,
      total: mapped.length,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? mapped.length,
    };
  },

  /** One candidate, found in the list — the API has no GET /candidates/:id. */
  async candidate(id: string): Promise<Candidate | null> {
    const rows = await apiTransport().get<ApiCandidate[]>('/api/v1/candidates');
    const row = rows.find((entry) => entry.id === id);
    return row ? toCandidate(row) : null;
  },

  /**
   * The pipeline panel, from the server's aggregation.
   *
   * The aggregate carries counts, not per-candidate dates, so two figures are
   * narrower than their labels: `interviewsThisWeek` is every scheduled
   * interview (the endpoint has no week window). `averageDaysToHire` is the
   * server's, from each hired candidate's application and hire dates.
   */
  async dashboard() {
    const data = await apiTransport().get<ApiRecruitmentDashboard>(
      '/api/v1/dashboards/recruitment',
    );

    const activeCandidates = Object.entries(data.candidatesByStage)
      .filter(([stage]) => !['hired', 'rejected', 'withdrawn'].includes(stage))
      .reduce((total, [, count]) => total + count, 0);

    return {
      // The server's recruitable state is 'approved' (mapped to 'open' here).
      openRequisitions:
        (data.requisitionsByStatus['approved'] ?? 0) + (data.requisitionsByStatus['open'] ?? 0),
      activeCandidates,
      interviewsThisWeek: data.interviewsScheduled,
      offersPending: data.candidatesByStage['offer'] ?? 0,
      averageDaysToHire: data.averageDaysToHire ?? null,
    };
  },

  /**
   * Move a candidate through the pipeline.
   *
   * The server holds the transition table and refuses a jump — applied
   * straight to hired, or a withdrawn candidate being revived. The adapter does
   * not duplicate the table: two copies of a state machine disagree eventually,
   * and the one in the browser is the one that will be wrong. The actor is the
   * session's; the argument keeps the prototype's signature.
   */
  async moveStage(id: string, stage: CandidateStage, _actor: AuditActor): Promise<Candidate> {
    return toCandidate(
      await apiTransport().post<ApiCandidate>(`/api/v1/candidates/${id}/move-stage`, { stage }),
    );
  },

  /** One candidate with its history, interviews and the moves open to it. */
  async candidateDetail(id: string): Promise<CandidateDetail> {
    const row = await apiTransport().get<
      ApiCandidate & {
        stageHistory: (Omit<CandidateStageEvent, 'fromStage' | 'toStage'> & {
          fromStage: string | null;
          toStage: string;
        })[];
        interviews: ApiInterview[];
        allowedStages: string[];
      }
    >(`/api/v1/candidates/${id}`);

    const history = row.stageHistory.map((event) => ({
      id: event.id,
      fromStage: event.fromStage
        ? narrow(CANDIDATE_STAGES, event.fromStage, 'candidate stage')
        : null,
      toStage: narrow(CANDIDATE_STAGES, event.toStage, 'candidate stage'),
      note: event.note,
      actorName: event.actorName,
      createdAt: event.createdAt,
    }));
    const closing = [...history].reverse().find((event) => event.toStage === row.stage);

    return {
      candidate: {
        ...toCandidate(row),
        // The reason a closed candidate was closed is the note on the move
        // that closed them; the candidate row has no column of its own for it.
        rejectionReason:
          row.stage === 'rejected' || row.stage === 'withdrawn' ? (closing?.note ?? null) : null,
      },
      history,
      interviews: row.interviews.map(toInterview),
      allowedStages: row.allowedStages.map((stage) =>
        narrow(CANDIDATE_STAGES, stage, 'candidate stage'),
      ),
      requisitionDepartmentId: row.requisition?.departmentId ?? null,
      requisitionPlacement: row.requisition
        ? {
            positionId: row.requisition.positionId ?? null,
            employmentType: row.requisition.employmentType ?? null,
            workLocationId: row.requisition.workLocationId ?? null,
            hiringManagerEmployeeId: row.requisition.hiringManagerEmployeeId ?? null,
          }
        : null,
    };
  },

  /**
   * Add a candidate against an approved requisition.
   *
   * The server refuses any other requisition; the form only offers approved
   * ones, and the refusal is shown verbatim if the list was stale.
   */
  async addCandidate(input: NewCandidateInput): Promise<Candidate> {
    return toCandidate(
      await apiTransport().post<ApiCandidate>('/api/v1/candidates', input, {
        idempotencyKey: newIdempotencyKey(),
      }),
    );
  },

  /** Reject a candidate, or record that they withdrew. The reason is required. */
  async closeCandidate(
    id: string,
    outcome: 'rejected' | 'withdrawn',
    reason: string,
  ): Promise<Candidate> {
    return toCandidate(
      await apiTransport().post<ApiCandidate>(`/api/v1/candidates/${id}/move-stage`, {
        stage: outcome,
        note: reason,
      }),
    );
  },

  /** Extend (or re-issue) an offer. Moves the candidate to offer stage. */
  async extendOffer(
    id: string,
    terms: { monthlyRate: number; startDate: string },
  ): Promise<Candidate> {
    return toCandidate(
      await apiTransport().post<ApiCandidate>(`/api/v1/candidates/${id}/offers`, terms, {
        idempotencyKey: newIdempotencyKey(),
      }),
    );
  },

  /** The candidate accepted. This is the only way a candidate becomes hired. */
  async acceptOffer(id: string): Promise<Candidate> {
    return toCandidate(
      await apiTransport().post<ApiCandidate>(`/api/v1/candidates/${id}/accept-offer`, {}),
    );
  },

  async scheduleInterview(
    candidateId: string,
    input: { scheduledFor: string; kind: string; interviewerName: string },
  ): Promise<CandidateInterviewRecord> {
    return toInterview(
      await apiTransport().post<ApiInterview>(
        `/api/v1/candidates/${candidateId}/interviews`,
        input,
        {
          idempotencyKey: newIdempotencyKey(),
        },
      ),
    );
  },

  async recordInterviewOutcome(
    candidateId: string,
    interviewId: string,
    input: InterviewOutcomeInput,
  ): Promise<CandidateInterviewRecord> {
    return toInterview(
      await apiTransport().post<ApiInterview>(
        `/api/v1/candidates/${candidateId}/interviews/${interviewId}/outcome`,
        input,
      ),
    );
  },

  /** The company-pattern work address, collision-checked by the server. */
  async suggestWorkEmail(candidateId: string): Promise<string> {
    const body = await apiTransport().get<{ workEmail: string }>(
      `/api/v1/candidates/${candidateId}/suggested-work-email`,
    );
    return body.workEmail;
  },

  /**
   * Turn a hired candidate into an employee.
   *
   * The most consequential write in the module: it creates a person, their
   * employment history, their salary and an onboarding case in one transaction.
   * The key matters more here than anywhere else — a retry after a timeout must
   * not produce a second employee, and the server's guard is a conditional
   * claim inside that transaction rather than a check before it.
   */
  async convertToEmployee(
    candidateId: string,
    placement: {
      departmentId: string;
      /** Defaults, on the server, to the requisition's employment type. */
      employmentType?: string;
      // `null` is "none", chosen; absent lets the server use the requisition's.
      positionId?: string | null;
      workLocationId?: string | null;
      payrollGroupId?: string | null;
      managerId?: string | null;
      startDate?: string;
      workEmail?: string;
    },
  ): Promise<{ employee: ConvertedEmployee; onboardingCaseId: string | null }> {
    const created = await apiTransport().post<ApiConvertedEmployee>(
      `/api/v1/candidates/${candidateId}/convert`,
      placement,
      { idempotencyKey: newIdempotencyKey() },
    );

    const employee: ConvertedEmployee = {
      id: created.id,
      employeeNumber: created.employeeNumber,
      fullName: created.fullName,
      firstName: created.firstName,
      middleName: created.middleName ?? '',
      lastName: created.lastName,
      workEmail: created.workEmail,
      departmentId: created.departmentId ?? '',
      employmentStatus: created.employmentStatus,
      employmentType: created.employmentType,
      dateHired: created.dateHired.slice(0, 10),
    };

    /*
     * The onboarding case is opened inside the same transaction but is not
     * carried back — the server returns the employee. Null, not an empty
     * string: the case exists and this call was never told its identifier, so
     * the screen finds it by employee.
     */
    return { employee, onboardingCaseId: null };
  },
};

interface ApiConvertedEmployee {
  id: string;
  employeeNumber: string;
  fullName: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  workEmail: string;
  departmentId: string | null;
  employmentStatus: string;
  employmentType: string;
  dateHired: string;
}

interface ApiCompensationRecord {
  id: string;
  employeeId: string;
  changeType: string;
  monthlyRate: string;
  dailyRate: string | null;
  hourlyRate: string | null;
  effectiveFrom: string;
  effectiveTo: string | null;
  reason: string | null;
  approvalStatus?: string;
  approvedByUserId: string | null;
  approvedAt: string | null;
  decisionNote?: string | null;
  createdBy?: string | null;
  createdByName?: string | null;
  approvedByName?: string | null;
  createdAt: string;
}

interface ApiCompensationOverview {
  history: ApiCompensationRecord[];
  current: ApiCompensationRecord | null;
  upcoming: ApiCompensationRecord[];
  policy?: { requiresApproval: boolean };
}

const COMPENSATION_APPROVAL: Record<string, CompensationRecord['approvalStatus']> = {
  approved: 'approved',
  pending: 'pending_hr',
  rejected: 'rejected',
  // Withdrawn by a separation: it would have started after the last working day.
  cancelled: 'cancelled',
};

/**
 * Decimal string to the frontend's minor-unit Money.
 *
 * The engine works in Decimal and the web app in integer centavos. Converting
 * through a float would lose the fourth decimal place the database keeps, so
 * this splits on the point and pads rather than multiplying by 100 — the
 * difference matters on an amount like 12345.675, where the float route rounds
 * the wrong way.
 */
function toMoney(value: string | null): Money | null {
  if (value === null) return null;

  const [whole, fraction = ''] = value.split('.');
  const centavos = `${fraction}00`.slice(0, 2);
  const sign = whole.startsWith('-') ? -1 : 1;
  const minorUnits = sign * (Math.abs(Number(whole)) * 100 + Number(centavos));

  return { minorUnits, currency: 'PHP' };
}

/**
 * The server has one `adjustment` where the console offers two kinds.
 *
 * Both market and one-time adjustments are stored as `adjustment` — sending
 * either name as-is was refused outright — and a stored `adjustment` reads back
 * as a market adjustment, the common case. The reason text carries which it was.
 */
function toApiChangeType(changeType: CompensationRecord['changeType']): string {
  return changeType === 'market_adjustment' || changeType === 'one_time_adjustment'
    ? 'adjustment'
    : changeType;
}

function toCompensationRecord(row: ApiCompensationRecord): CompensationRecord {
  return {
    id: row.id,
    employeeId: row.employeeId,
    changeType: narrow(
      COMPENSATION_CHANGE_TYPES,
      row.changeType === 'adjustment' ? 'market_adjustment' : row.changeType,
      'compensation change type',
    ),
    effectiveDate: row.effectiveFrom.slice(0, 10),
    // Not returned by the list; the previous rate is the next row down, and
    // the screen already has the ordered history to read it from.
    previousMonthlyRate: null,
    monthlyRate: toMoney(row.monthlyRate) ?? { minorUnits: 0, currency: 'PHP' },
    payFrequency: 'monthly',
    allowances: [],
    reason: row.reason ?? '',
    /*
     * Approved unless the organisation requires compensation approval, in
     * which case a new change is pending until a second person decides it.
     * Rows written before the column existed read as approved — they were in
     * force the moment they were written.
     */
    approvalStatus: COMPENSATION_APPROVAL[row.approvalStatus ?? 'approved'] ?? 'approved',
    approvedByUserId: row.approvedByUserId,
    approvedAt: row.approvedAt,
    decisionNote: row.decisionNote ?? null,
    createdByName: row.createdByName ?? null,
    approvedByName: row.approvedByName ?? null,
    supportingDocumentName: null,
    createdAt: row.createdAt,
    createdByUserId: row.createdBy ?? '',
  };
}

/**
 * Each approved record's predecessor rate, from the approved record before it.
 * The server does not send it; the ordered history already holds it.
 */
function withPreviousRates(newestFirst: CompensationRecord[]): CompensationRecord[] {
  return newestFirst.map((entry, index) => {
    const previous = newestFirst
      .slice(index + 1)
      .find((older) => older.approvalStatus === 'approved');
    return { ...entry, previousMonthlyRate: previous?.monthlyRate ?? null };
  });
}

export const apiCompensationService = {
  /**
   * An employee's salary history.
   *
   * Behind `compensation.view`, which HR holds and a manager does not. The
   * server refuses rather than returning a redacted list, because a list of
   * dates with the amounts blanked still tells a manager when their report last
   * got a raise.
   */
  async forEmployee(employeeId: string): Promise<CompensationRecord[]> {
    return (await apiCompensationService.overview(employeeId)).history;
  },

  /**
   * History, the rate in force today, what is already agreed for later, and
   * whether changes wait for approval — all from one server answer, so the
   * panel's "current" is the server's rule (latest approved record effective
   * on or before today) rather than "the newest row".
   */
  async overview(employeeId: string): Promise<CompensationOverview> {
    const body = await apiTransport().get<ApiCompensationOverview>(
      `/api/v1/employees/${employeeId}/compensation`,
    );
    const history = withPreviousRates(body.history.map(toCompensationRecord));
    const byId = new Map(history.map((entry) => [entry.id, entry]));
    return {
      history,
      current: body.current ? (byId.get(body.current.id) ?? null) : null,
      upcoming: body.upcoming
        .map((entry) => byId.get(entry.id))
        .filter((entry): entry is CompensationRecord => entry !== undefined),
      requiresApproval: body.policy?.requiresApproval ?? false,
    };
  },

  /** Approve or reject a pending change. Never by its recorder or its subject. */
  async decide(
    changeId: string,
    decision: 'approve' | 'reject',
    note: string,
  ): Promise<CompensationRecord> {
    return toCompensationRecord(
      await apiTransport().post<ApiCompensationRecord>(
        `/api/v1/compensation-changes/${changeId}/${decision}`,
        note.trim() ? { note: note.trim() } : {},
      ),
    );
  },

  /**
   * Record a change.
   *
   * Effective-dated and never edited in place. A raise recorded today with a
   * date last month does not rewrite last month's payroll — that period was
   * calculated against the compensation in force at the time, and correcting it
   * is a retroactive adjustment, not an edit.
   */
  async record(
    employeeId: string,
    input: Omit<
      CompensationRecord,
      | 'id'
      | 'employeeId'
      | 'createdAt'
      | 'createdByUserId'
      | 'approvalStatus'
      | 'approvedAt'
      | 'approvedByUserId'
    >,
  ): Promise<CompensationRecord> {
    const row = await apiTransport().post<ApiCompensationRecord>(
      `/api/v1/employees/${employeeId}/compensation-changes`,
      {
        changeType: toApiChangeType(input.changeType),
        effectiveFrom: input.effectiveDate,
        /*
         * A number, because the route's schema is `z.number().positive()` —
         * the codebase's money convention on input, shared with recurring
         * payroll items. A decimal string was refused outright ("expected
         * number, received string") on every change type.
         *
         * Precision survives: `minorUnits` is whole centavos, so dividing by
         * 100 yields a value with at most two decimals, and the server fixes
         * it to four places (`toFixed(4)`) before it reaches the Decimal
         * column. The rounding guards a fractional centavo from ever leaving.
         */
        monthlyRate: Math.round(input.monthlyRate.minorUnits) / 100,
        reason: input.reason,
      },
      { idempotencyKey: newIdempotencyKey() },
    );

    return toCompensationRecord(row);
  },
};
